import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ArrowLeft, ExternalLink, PlayCircle, CalendarCheck } from "lucide-react";
import { SEO } from "@/components/site/SEO";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import {
  FREEBIES, BOOKING_URL, BUDGET_SHEET_COPY_URL, BUDGET_TRAINING_VIDEO_ID, readFreebieAccess,
} from "@/lib/freebies";

const KEY = "budget-template";

const Step = ({ n, title, children }) => (
  <div className="flex gap-4">
    <span className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-full bg-gold/20 text-bronze font-serif text-lg">{n}</span>
    <div>
      <h3 className="font-serif text-lg text-legacy leading-tight">{title}</h3>
      <div className="mt-1.5 text-slate text-[0.92rem] leading-relaxed">{children}</div>
    </div>
  </div>
);

export default function BudgetTemplateThankYou() {
  const freebie = FREEBIES[KEY];
  const navigate = useNavigate();
  const [access, setAccess] = useState(undefined);

  useEffect(() => { setAccess(readFreebieAccess(KEY) || null); }, []);

  // The budget page's primary experience is the Google Sheets copy + video, so it
  // is available even without a stored download link; the Excel file is a backup.
  const hasAccess = access && (access.download_url || access.access_url);

  return (
    <div className="min-h-screen bg-ivory" data-testid="thankyou-page-budget-template">
      <SEO title="Budget Template + Training \u2014 Thank You | Legacy Life & Retirement" noindex />
      <Header />
      <main className="relative bg-ivory pt-[120px] md:pt-[150px] pb-24 md:pb-28 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" />
        <div className="container-lr relative max-w-3xl">
          {access === undefined ? null : !hasAccess ? (
            <div data-testid="thankyou-no-access" className="rounded-card bg-white border border-taupe/40 p-8 md:p-10 text-center">
              <h1 className="font-serif text-2xl md:text-3xl text-legacy leading-tight">Let's get you the Budget Template</h1>
              <p className="mt-4 text-slate leading-relaxed">
                It looks like you reached this page directly. Please request the
                {" "}<span className="text-legacy font-medium">Budget Template + Training</span> on the Resources page first.
              </p>
              <button type="button" onClick={() => navigate(`/resources#${freebie.anchor}`)}
                data-testid="thankyou-back-to-form" className="btn-base btn-primary mt-7 text-[0.8rem] tracking-[0.12em] uppercase">
                <ArrowLeft size={16} />Go to the form
              </button>
            </div>
          ) : (
            <>
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gold text-legacy"><Check size={26} /></span>
              <p className="eyebrow mt-6">Request received</p>
              <h1 className="mt-3 font-serif font-semibold tracking-display text-legacy text-3xl md:text-4xl leading-[1.08]">
                Your Budget Template and Training Are Ready
              </h1>
              <p className="mt-5 text-slate text-base md:text-lg leading-relaxed">
                Here's everything you need to get organized. Watch the short training, then open your
                own editable copy of the template and start plugging in your numbers.
              </p>

              <div className="mt-10 space-y-8">
                {/* Step 1 */}
                <Step n="1" title="Watch the training video">
                  Watch this first if you'd like guidance on how to use the template.
                  <div className="mt-4 aspect-video w-full rounded-card overflow-hidden bg-black border border-taupe/40">
                    <iframe className="h-full w-full" src={`https://www.youtube-nocookie.com/embed/${BUDGET_TRAINING_VIDEO_ID}?rel=0`}
                      title="Budget Template Training" loading="lazy" allowFullScreen
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      data-testid="budget-training-video" />
                  </div>
                </Step>

                {/* Step 2 */}
                <Step n="2" title="Open your personal budget template">
                  This link creates <span className="font-medium text-legacy">your own editable copy</span> in Google Sheets.
                  Replace the sample numbers with your own income, expenses, savings, and debt. Please
                  don't edit Shawn's original master file — your copy is completely private to you.
                  <div className="mt-4 flex flex-wrap gap-3">
                    <a href={BUDGET_SHEET_COPY_URL} target="_blank" rel="noopener noreferrer"
                      data-testid="budget-open-sheet" className="btn-base btn-gold text-[0.82rem] tracking-[0.1em] uppercase">
                      <ExternalLink size={16} />Open Your Budget Template
                    </a>
                  </div>
                </Step>

                {/* Step 3 */}
                <Step n="3" title="Book a call if you want help applying this">
                  Want help connecting this budget to your full financial system? Book a free strategy call.
                  <div className="mt-4">
                    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
                      data-testid="thankyou-book-call" className="btn-base btn-primary text-[0.82rem] tracking-[0.1em] uppercase">
                      <CalendarCheck size={16} />Book a Free Strategy Call
                    </a>
                  </div>
                </Step>
              </div>

              <div className="mt-10">
                <Link to="/resources" className="inline-flex items-center gap-2 text-sm text-bronze hover:text-gold">
                  <PlayCircle size={15} />Browse more resources
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
